import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import { max, orderBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import i18n from 'simple-react-i18n'
import Checkbox from '../../../components/forms/Checkbox'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import DataTypeApp from './DataTypeApp'
import ColorOval from '../../../components/icon/ColorOval'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { CardTable } from 'components/datatable/NewTable'
import { darkBlue } from 'utils/constants/ColorTheme'
import { formatData } from 'utils/ExportDataUtil'
import ExportAction from 'export/actions/ExportAction'
import { ShapeDataType } from 'administration/shapes/AdminShapes'

const DataTypesTable = ({
    readMode,
    dataTypes,
    project,
    onChange = () => {},
}) => {
    const [open, setOpen] = useState(false)
    const [dataType, setDataType] = useState({})

    const dispatch = useDispatch()

    const getNewMaxId = (p, maxId) => {
        // 6 ids are reserved for hydro
        if (p === 'SIH') {
            return maxId <= 6 ? 7 : maxId + 1
        }
        // 3 ids are reserved for other project
        return maxId <= 3 ? 4 : maxId + 1
    }

    const getTable = (data) => {
        const maxId = max(dataTypes.map(({ id }) => id))
        const newId = getNewMaxId(project, maxId)
        const actions = [{
            icon: 'note_add',
            tooltip: i18n.add,
            color: 'white',
            onClick: () => {
                setOpen(true)
                setDataType({ id: newId, project })
            },
            displayed: !readMode,
        }]

        const headers = [
            'name',
            'id',
            'version',
            { value: i18n.showData, key: 'showDataCheckbox' },
            { value: i18n.showSum, key: 'showSumCheckbox' },
            { value: i18n.warningTimeLimit, key: 'warningLimit' },
            { value: i18n.alertsTimeLimit, key: 'alertLimit' },
            'unit',
            'order',
            { value: i18n.calculMin, key: 'calculMinCheckbox' },
            { value: i18n.calculMoy, key: 'calculMoyCheckbox' },
            { value: i18n.calculMax, key: 'calculMaxCheckbox' },
            'codeParameter',
            { value: i18n.monitoringChronicle, key: 'monitoringChronicleCheckbox' },
            'codeUnit',
            'color',
        ]

        return (
            <CardTable
                title={ i18n.additionalData }
                rows={ data }
                actions={[
                    {
                        icon: 'download',
                        tooltip: i18n.export,
                        color: 'white',
                        onClick: () => dispatch(ExportAction.export(formatData(data), 'xlsx', i18n.dataTypes)),
                    },
                    ...actions,
                ]}
                rowsPerPageOptions={ nbPerPageLabel }
                headers={headers}
                color={darkBlue}
                lineActions={[{
                    icon: 'edit',
                    onClick: (s) => {
                        setOpen(true)
                        setDataType(s)
                    },
                    displayed: !readMode,
                }]}
            />
        )
    }

    const getPanel = () => {
        const data = orderBy(dataTypes, 'id')
        const dataFormatted = data.map(d => {
            return {
                ...d,
                showDataCheckbox: d.showData ? {
                    value: <Checkbox checked={ d.showData } disabled/>,
                    sortValue: !!d.showData,
                } : false,
                showSumCheckbox: d.showSum ? {
                    value: <Checkbox checked={ d.showSum } disabled />,
                    sortValue: !!d.showSum,
                } : false,
                calculMinCheckbox: d.calculMin ? {
                    value: <Checkbox checked={ d.calculMin } disabled />,
                    sortValue: !!d.calculMin,
                } : false,
                calculMoyCheckbox: d.calculMoy ? {
                    value: <Checkbox checked={ d.calculMoy } disabled />,
                    sortValue: !!d.calculMoy,
                } : false,
                calculMaxCheckbox: d.calculMax ? {
                    value: <Checkbox checked={ d.calculMax } disabled />,
                    sortValue: !!d.calculMax,
                } : false,
                monitoringChronicleCheckbox: d.monitoringChronicle ? {
                    value: <Checkbox checked={ d.monitoringChronicle } disabled />,
                    sortValue: !!d.monitoringChronicle,
                } : false,
                color: d.color ? {
                    value: <ColorOval color={d.color} />,
                    sortValue: d.color,
                } : false,
                colorValue: d.color,
            }
        })
        return getTable(dataFormatted)
    }

    const onSave = () => {
        const updatedDataTypes = [
            ...dataTypes.filter((d) => d.id !== dataType.id),
            { ...dataType, color: dataType.colorValue },
        ]
        setOpen(false)
        onChange(updatedDataTypes)
    }

    return (
        <>
            {getPanel()}
            <Dialog
                onClose={() => setOpen(false)}
                maxWidth='lg'
                open={open}
            >
                <DefaultDialogTitle title={i18n.modifyInfos} onClose={() => setOpen(false)} />
                <DialogContent>
                    <DataTypeApp onChange={(value) => setDataType({ ...dataType, ...value })} dataType={dataType} project={project}/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onSave} variant='contained' color='primary' disabled={!dataType.name} >
                        {i18n.validate}
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    )
}

DataTypesTable.propTypes = {
    dataTypes: PropTypes.arrayOf(ShapeDataType),
    project: PropTypes.oneOf(['SIES', 'SIQ', 'SIH', 'SIP']),
    readMode: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
}

export default DataTypesTable